import { BACKEND_URL } from "@/lib/backendUrl";
import { useCallback, useEffect, useRef, useState } from "react";
import { Camera, ThumbsUp, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Chip, Panel } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";

const API_BASE = `${BACKEND_URL}/api`;
const photoSrc = (url) => `${API_BASE}${String(url).replace(/^\/api/, "")}`;
const words = (s) => String(s || "").replace(/_/g, " ");

export const CandidateDirectory = ({ electionRef, onChanged }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [broken, setBroken] = useState({});
  const files = useRef({});

  const load = useCallback(async () => {
    const res = await api.get(`/elections/${electionRef}/candidates`);
    setData(res.data);
  }, [electionRef]);

  useEffect(() => { load().catch((e) => toast.error(apiError(e))); }, [load]);

  const support = async (ref, name) => {
    setBusy(ref);
    try {
      await api.post(`/elections/nominations/${ref}/support`);
      toast.success(`You are backing ${name}`);
      await load();
      onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const photo = async (ref, file) => {
    if (!file) return;
    setBusy(`photo-${ref}`);
    try {
      const body = new FormData();
      body.append("file", file);
      await api.post(`/elections/nominations/${ref}/photo`, body,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("The portrait is up — every card carries the same frame");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const removePhoto = async (ref) => {
    setBusy(`photo-${ref}`);
    try {
      await api.delete(`/elections/nominations/${ref}/photo`);
      toast.success("The portrait has been taken down");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  if (!data) return <Panel title="Candidates" testId="panel-directory-loading">
    <p className="text-[12.5px] text-muted-foreground">Opening the candidate directory…</p></Panel>;

  const c = data.criteria;

  return (
    <div className="space-y-4">
      <Panel title="How candidates are shown" testId="panel-directory-note"
             kicker={data.presentation.order_label}>
        <p className="text-[12.5px] leading-relaxed text-muted-foreground"
           data-testid="directory-note">
          {data.presentation.note}
        </p>
        <p className="mt-2 text-[11.5px] text-muted-foreground" data-testid="directory-criteria">
          Everyone listed meets the candidature rules for this election:{" "}
          {c.minimum_membership_months} month(s) in membership
          {c.must_be_paid_up ? ", paid up" : ""}
          {c.conflict_declaration_required ? ", conflicts declared" : ""}
          , at most {c.maximum_concurrent_positions} post(s) at once, at most{" "}
          {c.maximum_consecutive_terms} consecutive term(s), and {c.minimum_supporters} supporter(s)
          before the Commission will confirm them.
        </p>
        <div className="mt-2.5 flex flex-wrap gap-2 text-[11px]">
          <Chip testId="directory-count-confirmed"
                className="border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">
            {data.counters.confirmed} confirmed
          </Chip>
          {data.provisional && (
            <Chip testId="directory-count-gathering"
                  className="border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300">
              {data.counters.gathering_support} still gathering support
            </Chip>
          )}
          <Chip testId="directory-count-posts" className="border-border bg-muted text-muted-foreground">
            {data.counters.posts} post(s), {data.counters.posts_without_candidates} with nobody
            standing
          </Chip>
        </div>
      </Panel>

      {data.positions.map((p) => (
        <Panel key={p.position_ref} title={p.title} testId={`directory-post-${p.position_ref}`}
               kicker={`${p.seats} seat${p.seats > 1 ? "s" : ""} · ${p.method_label}`}
               actions={p.uncontested && (
                 <Chip testId={`uncontested-${p.position_ref}`}
                       className="border-primary/30 bg-primary/10 text-primary">uncontested</Chip>
               )}>
          {p.no_candidates ? (
            <p className="text-[12.5px] text-muted-foreground"
               data-testid={`no-candidates-${p.position_ref}`}>
              Nobody is standing for this post yet.
            </p>
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {p.candidates.map((cand) => (
                <div key={cand.nomination_ref}
                     data-testid={`candidate-${cand.nomination_ref}`}
                     className="flex h-full flex-col rounded-xl border border-border p-3.5">
                  <div className="flex items-start gap-3">
                    {cand.photo_url && !broken[cand.nomination_ref] ? (
                      <img alt="" data-testid={`candidate-photo-${cand.nomination_ref}`}
                           src={photoSrc(cand.photo_url)}
                           onError={() => setBroken((b) => ({ ...b,
                             [cand.nomination_ref]: true }))}
                           className="h-10 w-10 shrink-0 rounded-full object-cover" />
                    ) : (
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 font-mono text-[12px] text-primary"
                           data-testid={`candidate-initials-${cand.nomination_ref}`}>
                        {cand.initials}
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-[13px] font-semibold text-foreground"
                         data-testid={`candidate-name-${cand.nomination_ref}`}>
                        {cand.name}
                      </p>
                      <p className="text-[10.5px] text-muted-foreground">
                        {cand.nomination_ref} · {cand.statement_words} words
                        {cand.conflict_declared ? " · conflicts declared" : ""}
                      </p>
                    </div>
                  </div>
                  <p className="mt-2.5 whitespace-pre-line text-[12px] leading-relaxed text-muted-foreground">
                    {cand.statement}
                  </p>
                  {cand.biography && (
                    <p className="mt-2 text-[11.5px] leading-relaxed text-muted-foreground">
                      {cand.biography}
                    </p>
                  )}
                  {(cand.qualifications || cand.experience) && (
                    <p className="mt-2 text-[11px] text-muted-foreground">
                      {cand.qualifications}
                      {cand.qualifications && cand.experience ? " · " : ""}
                      {cand.experience}
                    </p>
                  )}
                  <div className="mt-auto flex flex-wrap items-center gap-2 pt-3">
                    <Chip testId={`candidate-status-${cand.nomination_ref}`}
                          className={cand.confirmed
                            ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                            : "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300"}>
                      {cand.confirmed ? "confirmed" : words(cand.status)}
                    </Chip>
                    {!cand.confirmed && (
                      <span className="font-mono text-[11px] text-muted-foreground"
                            data-testid={`candidate-support-${cand.nomination_ref}`}>
                        {cand.support_count}/{cand.minimum_supporters} supporters
                      </span>
                    )}
                    {data.provisional && !cand.confirmed && data.you_are_on_the_roll
                      && !cand.you_are_the_candidate && (
                      <Button size="sm" variant="outline" className="h-8 rounded-full"
                              data-testid={`directory-support-${cand.nomination_ref}`}
                              disabled={cand.you_supported || busy === cand.nomination_ref}
                              onClick={() => support(cand.nomination_ref, cand.name)}>
                        <ThumbsUp className="mr-1.5 h-3.5 w-3.5" />
                        {cand.you_supported ? "You back this" : "Support"}
                      </Button>
                    )}
                    {(cand.you_are_the_candidate || data.you_are_on_commission) && (
                      <>
                        <input type="file" accept="image/*" className="hidden"
                               data-testid={`photo-input-${cand.nomination_ref}`}
                               ref={(el) => { files.current[cand.nomination_ref] = el; }}
                               onChange={(e) => photo(cand.nomination_ref, e.target.files?.[0])} />
                        <Button size="sm" variant="ghost" className="h-8 rounded-full"
                                data-testid={`photo-button-${cand.nomination_ref}`}
                                disabled={busy === `photo-${cand.nomination_ref}`}
                                onClick={() => files.current[cand.nomination_ref]?.click()}>
                          <Camera className="mr-1.5 h-3.5 w-3.5" />
                          {cand.photo_url ? "Replace portrait" : "Add a portrait"}
                        </Button>
                        {cand.photo_url && (
                          <Button size="icon" variant="ghost" className="h-8 w-8"
                                  data-testid={`photo-remove-${cand.nomination_ref}`}
                                  onClick={() => removePhoto(cand.nomination_ref)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      ))}
    </div>
  );
};
